import { ChangeEvent, ComponentProps, ComponentType, createElement } from 'react';

export interface ChangeableValue<V, E> {
  onChange: (value: V | undefined, e: ChangeEvent<E>) => void;
}

export interface WithValue<V> {
  value?: V;
}

const getInputValue = <T, >(e: ChangeEvent<HTMLElement>) => {
  if (!('value' in e.target)) {
    throw new Error('Target element does not contain value');
  }
  return e.target.value as T;
};

/**
 * Allow component to implement ChangeableValue interface
 * @param Component
 * @param valueGetter
 */
export const withChangeableValue =
  <V = string, E extends HTMLElement = HTMLInputElement, C extends ComponentType<any> = ComponentType<any>>(
    Component: C,
    valueGetter: (e: ChangeEvent<E>) => V = getInputValue,
  ) =>
  ({ onChange, ...props }: Omit<ComponentProps<C>, 'onChange'> & ChangeableValue<V, E>) => {
    return createElement(Component, { ...(props as ComponentProps<C>), onChange: (e) => onChange(valueGetter(e), e) });
  };

/**
 * Allow any component to have value
 * @param Component
 * @param mapValue map value to wrapped component props
 */
export const withValue = <V = string, C extends ComponentType<any> = ComponentType<any>>(
  Component: C,
  mapValue: (value: V) => Partial<Record<keyof ComponentProps<C>, V>>,
) => {
  type Props = ComponentProps<C>;
  return ({ value, ...props }: Props & WithValue<V>) => createElement(Component, { ...props, ...mapValue(value) });
};
